import { useMemo } from 'react'
import { useInfiniteQuery, useQueries, useQuery } from 'react-query'
import { fetchAllAnalysis } from 'src/api/analysis/list'
import { fetchCohortFastqPairs } from 'src/api/analysis/cohort'
import { MULTISAMPLE_EXCLUDED_TYPES } from 'src/services/analysisStart'
import { describeRowWorkflow } from 'src/views/Batches/useBatches'

// Data for the group flows. Plain queries with no polling: useAnalysis polls every
// 15 s, which a picker does not need.

export const FINISHED_PAGE_SIZE = 100
export const COHORT_PAGE_SIZE = 50
// Pasted sample IDs: one query per ID, capped.
export const PASTE_ID_LIMIT = 30

// The backend stores a multisample run as analysis_type 'Multisample'.
export const isMultisampleType = (type) => String(type || '').toUpperCase() === 'MULTISAMPLE'

const rowSummary = (row) => {
  if (isMultisampleType(row.analysis_type)) return 'Multisample'
  const described = describeRowWorkflow(row)
  return [described.kindLabel, described.short].filter(Boolean).join(' · ')
}

// Cohort builds (GENESUS-COHORT, EXOMEDEPTH-COHORT, the older GATK-COHORT) and carrier
// runs have no per-sample variants to share.
const isExcludedType = (type) => MULTISAMPLE_EXCLUDED_TYPES.includes(type) || /-COHORT$/i.test(String(type || ''))

/** A finished-analyses row, from one /analysis/list row. */
export const finishedRowOf = (row) => ({
  fileId: row.file_id ?? null,
  analysisId: row.analysis_id ?? row.id ?? null,
  key: row.display_sample_name || row.sample_name || '',
  type: row.analysis_type || '',
  tags: Array.isArray(row.tags) ? row.tags : [],
  done: row.status === 'DONE',
  createdAt: row.created_at || null,
  summary: rowSummary(row),
})

/** /analysis/list rows a multisample group can take: cohorts and carrier runs are left out. */
export const finishedRows = (results) =>
  (Array.isArray(results) ? results : []).filter((row) => row && !isExcludedType(row.analysis_type)).map(finishedRowOf)

/**
 * Finished analyses, newest first, 100 a page, searched on the server by sample
 * name. `fetchNextPage` loads the next page ("Show more").
 */
export const useFinishedAnalyses = ({ search = '', enabled = true } = {}) => {
  const sampleName = search.trim()
  const query = useInfiniteQuery(
    ['new-analysis', 'finished', sampleName],
    ({ pageParam = 0 }) =>
      fetchAllAnalysis({ page: pageParam, limit: FINISHED_PAGE_SIZE, sample_name: sampleName || undefined }),
    {
      enabled,
      staleTime: 30 * 1000,
      refetchOnWindowFocus: false,
      keepPreviousData: true,
      getNextPageParam: (last, pages) => (last?.pagination?.has_next ? pages.length : undefined),
    },
  )
  const pages = query.data?.pages
  const rows = useMemo(() => {
    const seen = new Set()
    return (pages || [])
      .flatMap((page) => finishedRows(page?.results))
      .filter((row) => {
        const id = `${row.analysisId}:${row.fileId}`
        if (seen.has(id)) return false
        seen.add(id)
        return true
      })
  }, [pages])
  return {
    rows,
    total: pages?.[0]?.pagination?.total_count ?? rows.length,
    hasMore: Boolean(query.hasNextPage),
    fetchMore: () => query.fetchNextPage(),
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isFetchingMore: query.isFetchingNextPage,
    isError: query.isError,
  }
}

/**
 * Pasted sample IDs: one exact-name search each (at most PASTE_ID_LIMIT), for
 * "Add N as a group". Each ID resolves to its newest finished analysis.
 */
export const usePastedIds = (ids = [], { enabled = true } = {}) => {
  const list = ids.slice(0, PASTE_ID_LIMIT)
  const results = useQueries(
    list.map((id) => ({
      queryKey: ['new-analysis', 'finished-id', id],
      queryFn: () => fetchAllAnalysis({ page: 0, limit: 20, sample_name: id }),
      enabled,
      staleTime: 30 * 1000,
      refetchOnWindowFocus: false,
    })),
  )
  const isLoading = results.some((r) => r.isLoading)
  const matches = []
  const notFound = []
  results.forEach((r, i) => {
    const id = list[i]
    const rows = finishedRows(r.data?.results).filter(
      (row) => row.done && row.fileId && row.key.toLowerCase() === id.toLowerCase(),
    )
    if (rows.length) matches.push(rows[0])
    else if (!r.isLoading) notFound.push(id)
  })
  return { matches, notFound, isLoading, capped: ids.length > PASTE_ID_LIMIT }
}

// Every file name of a /cohort/list row: R1 and R2 of its FASTQ pair.
const cohortFiles = (row) =>
  [
    row.fastq1_filename || row.fastq_file_1?.original_name || row.fastq_file_1?.name || '',
    row.fastq2_filename || row.fastq_file_2?.original_name || row.fastq_file_2?.name || '',
  ].filter(Boolean)

/** A cohort candidate, from one /cohort/list row. */
export const cohortRowOf = (row) => ({
  pairId: row.fastqfilepair_id || row.id,
  key: row.sample_name || row.sample || '',
  files: cohortFiles(row),
  createdAt: row.created_at || row.uploaded_at || null,
})

/**
 * Finished germline FASTQ exome analyses on a kit (/cohort/list, 50 a page). The
 * server matches `bed_panel` on the start body's `panel`, the kit's BedFile name.
 */
export const useCohortCandidates = ({ kitId, search = '', field = 'sample', page = 0, enabled = true } = {}) => {
  const text = search.trim()
  const query = useQuery(
    ['new-analysis', 'cohort-candidates', kitId, text, field, page],
    () =>
      fetchCohortFastqPairs({
        page,
        limit: COHORT_PAGE_SIZE,
        sort_order: 'desc',
        [field === 'file' ? 'file_name' : 'sample_name']: text,
        bed_panel: kitId,
      }),
    { enabled: enabled && Boolean(kitId), keepPreviousData: true, staleTime: 30 * 1000, refetchOnWindowFocus: false },
  )
  const rows = useMemo(
    () => (Array.isArray(query.data?.results) ? query.data.results.map(cohortRowOf).filter((r) => r.pairId) : []),
    [query.data],
  )
  const pagination = query.data?.pagination || {}
  return {
    rows,
    total: pagination.total_count ?? rows.length,
    hasNext: Boolean(pagination.has_next),
    hasPrevious: page > 0,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isError: query.isError,
  }
}
